import { useMemo } from 'react';

import type { ColGroupDef } from 'ag-grid-enterprise';

import type { MultisByList } from '@enterprise/api';

import type { ListSegmentCellProps } from '@components/grid/renderers/types/list-segment-renderer.params';
import type { PercentCellProps } from '@components/grid/renderers/types/percent-cell-renderer.params';

import DateCellRenderer from '@components/grid/renderers/date-cell.renderer';
import ListSegmentRenderer from '@components/grid/renderers/list-segment-cell-renderer';
import NumberCellRenderer from '@components/grid/renderers/number-cell.renderer';
import PercentCellRenderer from '@components/grid/renderers/percent-cell.renderer';

export const useGetMultisByListColumns = () => {
  return useMemo(() => {
    return [
      {
        children: [
          {
            cellDataType: 'number',
            field: 'mailingId',
            headerClass: ['bg-blue-900'],
            headerName: 'ID',
            hide: true,
            width: 125,
          },
          {
            cellDataType: 'date',
            cellRenderer: DateCellRenderer,
            field: 'mailDate',
            headerClass: ['bg-blue-900'],
            headerName: 'Mail Date',
            width: 175,
          },
          {
            field: 'mailingDisplayName',
            headerClass: ['bg-blue-900'],
            headerName: 'Name',
            width: 300,
          },
          {
            field: 'mailCode',
            headerClass: ['bg-blue-900'],
            headerName: 'Mail Code',
            width: 150,
          },
          {
            cellRenderer: ListSegmentRenderer,
            cellRendererParams: (params: ListSegmentCellProps<MultisByList>) => {
              return {
                ...params,
                header: 'listDisplayName',
                subHeader: 'listManagerName',
              } as ListSegmentCellProps<MultisByList>;
            },
            field: 'listDisplayName',
            headerClass: ['bg-blue-900'],
            headerName: 'List/Segment',
            width: 300,
            wrapText: true,
          },
        ],
        headerClass: ['bg-blue-950'],
        headerName: 'Mailing Information',
      },
      {
        children: [
          {
            aggFunc: 'sum',
            cellRenderer: NumberCellRenderer,
            field: 'quantityOrdered',
            filter: 'agNumberColumnFilter',
            headerClass: ['bg-blue-800'],
            headerName: 'Requested',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellRenderer: NumberCellRenderer,
            field: 'quantityReceived',
            filter: 'agNumberColumnFilter',
            headerClass: ['bg-blue-800'],
            headerName: 'Received',
            type: ['numericColumn'],
            width: 150,
          },
        ],
        headerClass: ['bg-blue-950'],
        headerName: '',
      },
      {
        children: [
          {
            aggFunc: 'sum',
            cellDataType: 'number',
            cellRenderer: NumberCellRenderer,
            field: 'quantitySameListDupes',
            headerClass: ['bg-purple-900'],
            headerName: 'Same List',
            type: ['rightAligned'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellDataType: 'number',
            cellRenderer: NumberCellRenderer,
            field: 'quantitySameGroupDupes',
            headerClass: ['bg-purple-900'],
            headerName: 'Same Group',
            type: ['rightAligned'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellDataType: 'number',
            cellRenderer: NumberCellRenderer,
            field: 'quantityTotalDupes',
            headerClass: ['bg-purple-900'],
            headerName: 'Total Dupes',
            type: ['rightAligned'],
            width: 150,
          },
        ],
        headerClass: ['bg-purple-950'],
        headerGroupComponentParams: {
          icon: 'fa-people-arrows',
        },
        headerName: 'Match Counts',
      },
      {
        children: [
          {
            cellClass: ['bg-green-400', 'text-green-900'],
            cellRenderer: PercentCellRenderer,
            cellRendererParams: (params: PercentCellProps<MultisByList, number>) => {
              return {
                api: params.api,
                grandTotalFields: ['quantityReceived'],
                node: params.node,
                totalFields: ['quantityTotalDupes'],
                value: params.value,
              };
            },
            field: 'dupePercentage',
            filter: 'agNumberColumnFilter',
            headerClass: ['bg-green-800'],
            headerName: 'Dupes %',
            type: ['numericColumn'],
            width: 150,
          },
        ],
        headerClass: ['bg-green-950'],
        headerName: '',
      },
    ] as ColGroupDef<MultisByList>[];
  }, []);
};
